import {
	BlogHomeCustomization,
	BlogPostCustomization,
	CustomPageType,
	FooterCustomization,
	HeaderCustomization,
} from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { MastheadPortalId } from '../../../../models/AdminModels';
import {
	invalidateHostedBlogPagePreview,
	invalidateSchedulerPreviewQuery,
	invalidateSurveyPreviewQuery,
	useTemplateDesignerPreviewUrlsQuery,
} from '../../../../queries';
import {
	invalidateGetBlogStylesByTypeQueries,
	useGetBlogStylesByTypeQuery,
} from '../../../../queries/CustomPageStyle/useGetBlogStylesByTypeQuery';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { bs } from '../../../../web/styles/styles';
import { useAccountContext } from '../../../components/blogs/AccountContext';
import { BreadcrumbsBarWithNameProvider } from '../../../components/blogs/BreadcrumbsBarWithNameProvider';
import { HtmlPreviewFrame } from '../../../components/blogs/HtmlPreviewFrame';
import { FooterDesignForm } from './Forms/FooterDesignForm';
import { HeaderDesignForm } from './Forms/HeaderDesignForm';
import { HomeDesignForm } from './Forms/HomeDesignForm';
import { PostDesignForm } from './Forms/PostDesignForm';
import { PreviewContext, PreviewContextTabs } from './PreviewContextTabs';
import { PresetSelector } from './PresetSelector';
import { StylePreset } from './presets';
import { styleSheet } from './styles';

export const TemplateDesignFormContainer: FC = () => {
	const { account, impersonationContext } = useAccountContext();

	const blogFeature = account?.features?.blogFeature;
	const isBlogFeatureEnabled = blogFeature?.enabled;
	const isDudaHosted = !!blogFeature?.dudaSiteId;
	const isLevitateBlogEnabled = !isDudaHosted && isBlogFeatureEnabled;

	const surveyFeature = account?.features?.surveys;
	const isSurveyFeatureEnabled = surveyFeature?.enabled ?? false;
	const schedulerFeature = account?.features?.meetingScheduler;
	const isSchedulerFeatureEnabled = schedulerFeature?.enabled ?? false;

	// Fetch preview URLs to determine if surveys/meetings actually exist
	const { data: previewUrls } = useTemplateDesignerPreviewUrlsQuery({
		impersonationContext,
		enabled: !!impersonationContext,
	});

	// Combine feature availability with actual content availability
	const isSurveyAvailable = isSurveyFeatureEnabled && (previewUrls?.surveyAvailable ?? false);
	const isMeetingAvailable = isSchedulerFeatureEnabled && (previewUrls?.meetingAvailable ?? false);

	const [isDirty, setIsDirty] = useState<boolean>(false); // data in form has been edited, not saved
	const [selectedPageType, setSelectedPageType] = useState<CustomPageType>(CustomPageType.Header);
	const [pendingTab, setPendingTab] = useState<CustomPageType | null>(null);
	const [currentPresetId, setCurrentPresetId] = useState<string>('custom');
	const [previewContext, setPreviewContext] = useState<PreviewContext>('blog');

	const headerFormRef = useRef<{ applyPreset: (preset: StylePreset) => void }>(null);
	const footerFormRef = useRef<{ applyPreset: (preset: StylePreset) => void }>(null);

	const { data, isSuccess } = useGetBlogStylesByTypeQuery({
		impersonationContext,
		type: selectedPageType,
		meta: {
			toastErrorMessage: 'Error loading custom blog styles',
		},
	});
	useEffect(() => {
		if (isSuccess) {
			setIsDirty(false);
		}
	}, [isSuccess]);

	const handleFormUpdateSuccess = () => {
		invalidateGetBlogStylesByTypeQueries(selectedPageType);
		invalidateHostedBlogPagePreview(selectedPageType);
		invalidateSurveyPreviewQuery();
		invalidateSchedulerPreviewQuery();
		setIsDirty(false);
	};

	const handleValueChange = (value: CustomPageType) => {
		if (!isDirty) {
			setSelectedPageType(value);
		} else {
			setPendingTab(value);
		}
	};

	const handleDialogClose = (_: never, canceled: boolean) => {
		if (!canceled) {
			setSelectedPageType(pendingTab);
			setIsDirty(false);
		}
		setPendingTab(null);
	};

	const dynamicTabStyles = (pageType: CustomPageType) => {
		return selectedPageType === pageType ? styleSheet.selectedTab : styleSheet.backgroundTab;
	};

	const handleFormDirty = useCallback(() => {
		setIsDirty(true);
		setCurrentPresetId('custom');
	}, []);

	const handlePresetSelect = useCallback((preset: StylePreset) => {
		setCurrentPresetId(preset.id);

		// Apply preset to Header and Footer only (not blog-specific forms)
		if (headerFormRef.current) {
			headerFormRef.current.applyPreset(preset);
		}
		if (footerFormRef.current) {
			footerFormRef.current.applyPreset(preset);
		}

		setIsDirty(true);
	}, []);

	const showPreviewContextTabs =
		selectedPageType === CustomPageType.Header || selectedPageType === CustomPageType.Footer;

	return (
		<div className='admin-blog-design-form-container'>
			{account ? (
				<>
					<MultiContainerHeader
						appBarHeader={<BreadcrumbsBarWithNameProvider account={account} currentLocation='Template Designer' />}
						appBarPortalDestinationId={MastheadPortalId}
					/>
					<div className={css(bs.px6, bs.pt6)}>
						<PresetSelector
							currentPresetId={currentPresetId}
							onPresetSelect={handlePresetSelect}
							disabled={false}
							hasUnsavedChanges={isDirty}
						/>
					</div>
					<Tabs.Root
						className={css(styleSheet.tabRoot)}
						onValueChange={handleValueChange as (value: string) => void}
						value={selectedPageType}
					>
						<Tabs.List className={css(styleSheet.tabList)}>
							<Tabs.Trigger
								className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.Header))}
								value={CustomPageType.Header}
							>
								Header
							</Tabs.Trigger>
							<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
							{isLevitateBlogEnabled && (
								<>
									<Tabs.Trigger
										className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.BlogHome))}
										value={CustomPageType.BlogHome}
									>
										Blog Home
									</Tabs.Trigger>
									<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
									<Tabs.Trigger
										className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.BlogPost))}
										value={CustomPageType.BlogPost}
									>
										Blog Post
									</Tabs.Trigger>
									<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
								</>
							)}
							<Tabs.Trigger
								className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.Footer))}
								value={CustomPageType.Footer}
							>
								Footer
							</Tabs.Trigger>
							<div className={css(styleSheet.tabListBorder, bs.wFull)} />
						</Tabs.List>
						<Tabs.Content value={CustomPageType.Header}>
							<HeaderDesignForm
								ref={headerFormRef}
								customContent={data?.type === CustomPageType.Header ? (data as HeaderCustomization) : undefined}
								onFormDirty={handleFormDirty}
								onUpdateSuccess={handleFormUpdateSuccess}
							/>
						</Tabs.Content>
						{isLevitateBlogEnabled && (
							<>
								<Tabs.Content value={CustomPageType.BlogHome}>
									<HomeDesignForm
										customContent={data?.type === CustomPageType.BlogHome ? (data as BlogHomeCustomization) : undefined}
										onFormDirty={handleFormDirty}
										onUpdateSuccess={handleFormUpdateSuccess}
									/>
								</Tabs.Content>
								<Tabs.Content value={CustomPageType.BlogPost}>
									<PostDesignForm
										customContent={data?.type === CustomPageType.BlogPost ? (data as BlogPostCustomization) : undefined}
										onFormDirty={handleFormDirty}
										onUpdateSuccess={handleFormUpdateSuccess}
									/>
								</Tabs.Content>
							</>
						)}
						<Tabs.Content value={CustomPageType.Footer}>
							<FooterDesignForm
								ref={footerFormRef}
								customContent={data?.type === CustomPageType.Footer ? (data as FooterCustomization) : undefined}
								onFormDirty={handleFormDirty}
								onUpdateSuccess={handleFormUpdateSuccess}
							/>
						</Tabs.Content>
					</Tabs.Root>
					<div className={css(bs.pb7)} />
					{showPreviewContextTabs && (
						<div className={css(bs.px6, bs.pb4)}>
							<PreviewContextTabs
								selectedContext={previewContext}
								onContextChange={setPreviewContext}
								blogFeatureEnabled={isLevitateBlogEnabled}
								blogAvailable={isLevitateBlogEnabled}
								surveyFeatureEnabled={isSurveyFeatureEnabled}
								surveyAvailable={isSurveyAvailable}
								meetingFeatureEnabled={isSchedulerFeatureEnabled}
								meetingAvailable={isMeetingAvailable}
							/>
						</div>
					)}
					<HtmlPreviewFrame type={selectedPageType} previewContext={showPreviewContextTabs ? previewContext : 'blog'} />
				</>
			) : (
				<LoadingSpinner />
			)}
			{pendingTab && (
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{ isOpen: !!pendingTab, onRequestClose: handleDialogClose }}
					options={DefaultConfirmationOptions}
					title='Changing tabs without saving will clear pending edits'
				/>
			)}
		</div>
	);
};
